import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faPen, faTrash, faDownload } from '@fortawesome/free-solid-svg-icons';
import { MenuBarComponent } from '../../core/components/menu-bar/menu-bar.component';
import { FooterComponent } from '../../core/components/footer/footer.component';
import { AdminNavComponent } from '../../core/components/admin-nav/admin-nav.component';
import { FileModalComponent, FileSavePayload } from '../../core/components/file-modal/file-modal.component';
import { SessionService } from '../../core/services/session.service';
import { AdminFilesService, AdminFilePayload } from '../../core/services/admin-files.service';
import { AdminCategoriesService } from '../../core/services/admin-categories.service';
import { isViewableInBrowser } from '../../core/utils/file-types';

interface AdminFile {
    id: string;
    title: string;
    descr?: string;
    realFilename?: string;
    categoryId?: string | null;
    categoryName?: string | null;
    url?: string;
}

@Component({
    selector: 'app-files',
    imports: [CommonModule, FormsModule, FontAwesomeModule, NgbTooltip, MenuBarComponent, FooterComponent, AdminNavComponent],
    templateUrl: './files.component.html',
    styleUrl: './files.component.scss',
})
export class FilesComponent implements OnInit {
    files: AdminFile[] = [];
    categories: { id: string; categoryName: string }[] = [];
    loading = false;
    search = '';

    readonly faPen = faPen;
    readonly faTrash = faTrash;
    readonly faDownload = faDownload;

    constructor(
        private filesService: AdminFilesService,
        private categoriesService: AdminCategoriesService,
        private session: SessionService,
        private router: Router,
        private modalService: NgbModal,
        private toastr: ToastrService,
    ) { }

    ngOnInit(): void {
        if (!this.session.isLoggedIn() || !this.session.isAdmin()) {
            this.router.navigate(['/']);
            return;
        }
        this.loadCategories();
        this.load();
    }

    get filteredFiles(): AdminFile[] {
        const q = this.search.trim().toLowerCase();
        if (!q) return this.files;
        return this.files.filter((f) =>
            (f.title || '').toLowerCase().includes(q) ||
            (f.realFilename || '').toLowerCase().includes(q) ||
            (f.categoryName || '').toLowerCase().includes(q),
        );
    }

    loadCategories(): void {
        this.categoriesService.list().subscribe({
            next: (res) => { this.categories = res?.categories ?? []; },
            error: () => { this.categories = []; },
        });
    }

    load(): void {
        this.loading = true;
        this.filesService.list().subscribe({
            next: (res) => { this.files = res?.files ?? []; this.loading = false; },
            error: () => { this.files = []; this.loading = false; },
        });
    }

    openFileModal(file?: AdminFile): void {
        const ref = this.modalService.open(FileModalComponent, {
            centered: true,
            windowClass: 'auth-modal-window',
            // No dismissing (backdrop, Escape or the X) mid-upload: the request
            // would carry on with the modal gone and nothing to report back.
            beforeDismiss: () => !ref.componentInstance?.submitting,
        });
        ref.componentInstance.categories = this.categories;
        if (file) {
            ref.componentInstance.file = {
                id: file.id,
                title: file.title,
                descr: file.descr,
                categoryId: file.categoryId ?? null,
                realFilename: file.realFilename,
            };
        }
        // The modal runs this and holds itself open until it completes, so the
        // result below only ever fires on success.
        ref.componentInstance.saveHandler = (data: FileSavePayload) => {
            const payload: AdminFilePayload = {
                title: data.title!,
                descr: data.descr,
                categoryId: data.categoryId || '',
                ...(data.fileBase64
                    ? { fileBase64: data.fileBase64, realFilename: data.realFilename, contentType: data.contentType }
                    : {}),
            };
            return data.id
                ? this.filesService.update(data.id, payload)
                : this.filesService.create(payload);
        };
        ref.result.then(
            (data: any) => {
                if (!data) return;
                this.toastr.success(data.id ? 'File updated' : 'File uploaded');
                this.load();
            },
            () => { /* dismissed */ },
        );
    }

    download(f: AdminFile): void {
        // Decided here, not after the response: a tab opened outside the click
        // handler is treated as a popup and blocked.
        const viewable = isViewableInBrowser(f.realFilename);
        const win = viewable ? window.open('', '_blank') : null;

        this.filesService.getDownloadUrl(f.id, viewable).subscribe({
            next: (res) => {
                if (!res?.url) {
                    win?.close();
                    this.toastr.error('Unable to download file');
                    return;
                }
                if (viewable) {
                    // renders in the tab: the URL carries no attachment header
                    if (win) win.location.href = res.url; else window.open(res.url, '_blank');
                    return;
                }
                // saved to disk under the real filename, no tab involved
                const link = document.createElement('a');
                link.href = res.url;
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                link.remove();
            },
            error: () => {
                win?.close();
                this.toastr.error('Unable to download file');
            },
        });
    }

    remove(f: AdminFile): void {
        if (!confirm(`Delete file "${f.title}"?`)) return;
        this.filesService.delete(f.id).subscribe({
            next: () => { this.toastr.success('File deleted'); this.load(); },
            error: () => this.toastr.error('Unable to delete file'),
        });
    }
}
